"use client";

import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import "@/src/components/Home/LandingV6/landing-v6.css";
import {
  Nav,
  Button,
  StartWritingButton,
  QuarklePlanet,
  Mascot,
  Wordmark,
  QuarkleLogo,
} from "@/src/components/Home/LandingV6/ui";
import { Footer } from "@/src/components/Home/LandingV6/blocks";
import {
  Sparkle,
  Star,
  WaveRule,
  Asterism,
  OutlineDisplay,
  Ribbon,
  TapeSticker,
  CornerPin,
  BaselineGrid,
} from "@/src/components/Home/LandingV6/ornaments";

function Section({ label, title, italicWord, body, children, dark, className }) {
  return (
    <section
      className={className}
      style={{
        padding: "100px 40px",
        background: dark ? "var(--ink)" : "transparent",
        color: dark ? "var(--paper)" : "inherit",
        borderTop: "1px solid var(--ink)",
        position: "relative",
      }}
    >
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        {label && <span className="caps" style={{ color: "var(--pink)" }}>{label}</span>}
        {title && (
          <h2 className="tanker" style={{ fontSize: "clamp(42px, 5.6vw, 88px)", margin: "14px 0 18px", lineHeight: 0.96, letterSpacing: "-0.03em" }}>
            {title}{" "}
            {italicWord && (
              <span className="display" style={{ fontStyle: "italic", color: "var(--pink)" }}>
                {italicWord}
              </span>
            )}
            {title && title.endsWith(".") ? null : "."}
          </h2>
        )}
        {body && (
          <p className="display" style={{ fontSize: 20, fontStyle: "italic", opacity: 0.85, maxWidth: 780, lineHeight: 1.55 }}>
            {body}
          </p>
        )}
        {children}
      </div>
    </section>
  );
}

function FeatureRow({ n, title, body, Icon, accent = "var(--pink)", tape }) {
  return (
    <article
      style={{
        display: "grid",
        gridTemplateColumns: "auto 1fr",
        gap: 28,
        alignItems: "flex-start",
        padding: "32px 0",
        borderTop: "1px dashed var(--rule)",
      }}
    >
      <div style={{ width: 100 }}>
        <span
          className="tanker"
          style={{
            fontSize: 96,
            fontStyle: "italic",
            color: accent,
            lineHeight: 0.8,
            display: "inline-block",
          }}
        >
          {n}
        </span>
      </div>
      <div>
        {tape && (
          <div style={{ marginBottom: 10 }}>
            <TapeSticker color="var(--lime)" rotate={-3}>{tape}</TapeSticker>
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 8 }}>
          {Icon && <span style={{ color: accent }}><Icon /></span>}
          <h3 className="tanker" style={{ fontSize: "clamp(30px, 3.2vw, 48px)", lineHeight: 1, letterSpacing: "-0.025em", margin: 0 }}>
            {title}
          </h3>
        </div>
        <p className="display" style={{ fontSize: 18, lineHeight: 1.6, fontStyle: "italic", opacity: 0.88, maxWidth: 680, margin: 0 }}>
          {body}
        </p>
      </div>
    </article>
  );
}

function ProUnlocksPanel() {
  const unlocks = [
    { n: "01", label: "open expression", detail: "mature themes, violence, no content wall", color: "var(--pink-deep)", Icon: IconShield },
    { n: "02", label: "every model in chat", detail: "not just auto, pick per message", color: "var(--violet)", Icon: IconRoom },
    { n: "03", label: "the deeper editorial reports", detail: "developmental & full assessment", color: "var(--clay)", Icon: IconMemory },
    { n: "04", label: "200x the daily usage", detail: "stop counting revisions", color: "var(--moss)", Icon: IconBolt },
  ];
  return (
    <div
      style={{
        background: "var(--paper-3)",
        border: "1.5px solid var(--ink)",
        borderRadius: 16,
        padding: 24,
        boxShadow: "10px 10px 0 var(--ink)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
        <span className="tanker" style={{ fontSize: 22, fontStyle: "italic" }}>
          what pro unlocks
        </span>
        <span
          className="mono"
          style={{
            fontSize: 10,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            background: "var(--pink-deep)",
            color: "#fff",
            padding: "6px 11px",
            borderRadius: 999,
            letterSpacing: "0.06em",
          }}
        >
          <span style={{ width: 6, height: 6, background: "var(--lime)", borderRadius: 999, animation: "qv6-qpulse 1.4s infinite" }} />
          pro only
        </span>
      </div>
      <div style={{ display: "grid", gap: 10 }}>
        {unlocks.map((u) => (
          <div
            key={u.label}
            style={{
              display: "grid",
              gridTemplateColumns: "auto auto 1fr",
              gap: 14,
              alignItems: "center",
              background: "var(--paper-2)",
              border: "1px solid var(--rule)",
              borderLeft: `4px solid ${u.color}`,
              borderRadius: 10,
              padding: "10px 14px",
            }}
          >
            <span className="tanker" style={{ fontSize: 26, fontStyle: "italic", color: u.color, lineHeight: 1, opacity: 0.4 }}>
              {u.n}
            </span>
            <span
              style={{
                width: 30,
                height: 30,
                borderRadius: "50%",
                background: `color-mix(in srgb, ${u.color} 18%, var(--paper))`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: u.color,
                flexShrink: 0,
              }}
            >
              <u.Icon />
            </span>
            <div>
              <div className="caps" style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)" }}>
                {u.label}
              </div>
              <div className="mono" style={{ fontSize: 11, opacity: 0.6, marginTop: 3 }}>
                {u.detail}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PricingCard({ cycle, setCycle }) {
  return (
    <div
      style={{
        background: "var(--paper-3)",
        color: "var(--ink)",
        border: "1.5px solid var(--ink)",
        borderRadius: 14,
        padding: "32px 30px",
        boxShadow: "10px 10px 0 var(--pink)",
        maxWidth: 440,
        position: "relative",
      }}
    >
      <div style={{ position: "absolute", top: -14, left: 24 }}>
        <Ribbon color="var(--lime)" rotate={-4} fg="var(--ink)">most writers pick this</Ribbon>
      </div>
      <div
        style={{
          display: "inline-flex",
          gap: 2,
          padding: 3,
          background: "var(--paper-2)",
          borderRadius: 999,
          marginBottom: 14,
        }}
      >
        {[
          { k: "monthly", label: "monthly" },
          { k: "yearly", label: "yearly · save 50%" },
        ].map((o) => {
          const on = cycle === o.k;
          return (
            <button
              key={o.k}
              onClick={() => setCycle(o.k)}
              className="mono"
              style={{
                all: "unset",
                cursor: "pointer",
                padding: "6px 14px",
                borderRadius: 999,
                fontSize: 10,
                textTransform: "uppercase",
                letterSpacing: "0.14em",
                color: on ? "#fff" : "var(--ink)",
                background: on ? "var(--pink)" : "transparent",
                fontWeight: on ? 600 : 500,
                transition: "all 0.2s ease",
              }}
            >
              {o.label}
            </button>
          );
        })}
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 8 }}>
        <span className="tanker" style={{ fontSize: 72, lineHeight: 0.9 }}>
          {cycle === "monthly" ? "$4.99" : "$120"}
        </span>
        <span className="mono" style={{ fontSize: 17, opacity: 1, fontWeight: 600 }}>
          {cycle === "monthly" ? "first month · then $19.99/mo" : "per year · save 50%"}
        </span>
      </div>
      <p className="display" style={{ fontStyle: "italic", fontSize: 16, opacity: 0.85, marginTop: 10 }}>
        everything on the free plan, plus:
      </p>
      <ul style={{ listStyle: "none", padding: 0, margin: "16px 0", display: "grid", gap: 10 }}>
        {[
          "open expression: mature themes, violence, no content wall",
          "every model in chat, not just auto",
          "developmental and full assessment reports",
          "custom reviewers, built from your own instructions",
          "50 Drawing Room pictures a day · no fixed gallery limit",
          "200x the daily usage",
        ].map((f) => (
          <li key={f} style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 10, alignItems: "start" }}>
            <span style={{ color: "var(--lime)", marginTop: 3 }}>
              <svg width="14" height="14" viewBox="0 0 24 24">
                <path d="M4 12 L10 18 L20 6" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <span style={{ fontSize: 14 }}>{f}</span>
          </li>
        ))}
      </ul>
      <StartWritingButton variant="pink" size="lg" style={{ width: "100%", justifyContent: "center" }}>
        {cycle === "monthly" ? "start pro · $4.99 first month →" : "start pro · $120/yr →"}
      </StartWritingButton>
      <div style={{ marginTop: 10, textAlign: "center" }}>
        <span className="mono" style={{ fontSize: 10, opacity: 0.65, letterSpacing: "0.14em", textTransform: "uppercase" }}>
          cancel any time
        </span>
      </div>
    </div>
  );
}

/* Minimal bespoke inline icons to keep the page consistent. */
const IconRoom = () => (
  <svg width="24" height="24" viewBox="0 0 28 28" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
    <rect x="3" y="6" width="22" height="16" rx="2" />
    <path d="M8 6 L8 22" opacity="0.5" />
    <path d="M14 6 L14 22" opacity="0.5" />
    <path d="M20 6 L20 22" opacity="0.5" />
  </svg>
);
const IconMemory = () => (
  <svg width="24" height="24" viewBox="0 0 28 28" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
    <circle cx="14" cy="14" r="10" />
    <path d="M8 14 L12 18 L20 10" />
  </svg>
);
const IconBolt = () => (
  <svg width="24" height="24" viewBox="0 0 28 28" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round">
    <path d="M16 2 L5 16 L13 16 L12 26 L23 12 L15 12 Z" />
  </svg>
);
const IconShield = () => (
  <svg width="24" height="24" viewBox="0 0 28 28" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
    <path d="M14 3 L24 7 L24 15 C 24 20 19 24 14 25 C 9 24 4 20 4 15 L 4 7 Z" />
    <path d="M10 14 L13 17 L19 10" />
  </svg>
);
const IconImage = () => (
  <svg width="24" height="24" viewBox="0 0 28 28" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="22" height="20" rx="2" />
    <circle cx="19" cy="10" r="2.5" />
    <path d="M5 21 L11 14 L15 18 L18 15 L24 21" />
  </svg>
);

export default function ProPage() {
  const [theme, setTheme] = useState("paper");
  const [cycle, setCycle] = useState("monthly");
  const { loginWithRedirect } = useAuth0();
  useEffect(() => {
    try {
      const s = window.localStorage.getItem("qv6-theme");
      if (s === "paper" || s === "midnight") setTheme(s);
    } catch (_) {}
  }, []);
  return (
    <div className={`qv6 ${theme === "midnight" ? "theme-midnight" : ""}`}>
      <Nav />

      {/* Hero */}
      <section style={{ padding: "80px 40px 60px", position: "relative", overflow: "hidden" }}>
        <div aria-hidden style={{ position: "absolute", top: 40, right: -40, opacity: 0.1, pointerEvents: "none" }}>
          <OutlineDisplay text="pro." size={300} color="var(--pink)" strokeW={1.8} />
        </div>
        <div aria-hidden style={{ position: "absolute", bottom: 40, left: 80 }}>
          <Sparkle size={40} color="var(--pink)" style={{ animation: "qv6-sparkle 3s infinite" }} />
        </div>
        <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative" }}>
          <div style={{ display: "inline-flex", gap: 12, alignItems: "center", marginBottom: 18 }}>
            <WaveRule w={50} color="var(--ink)" />
            <span className="caps" style={{ color: "var(--pink)" }}>quarkle pro · for the book you're finishing</span>
          </div>
          <h1
            className="tanker"
            style={{
              fontSize: "clamp(60px, 8.2vw, 160px)",
              lineHeight: 0.86,
              letterSpacing: "-0.04em",
              margin: "0 0 24px",
            }}
          >
            the full <span className="display" style={{ fontStyle: "italic", color: "var(--pink)" }}>room</span>,
            <br />
            unlocked.
          </h1>
          <div style={{ display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: 48, alignItems: "center", marginTop: 32 }}>
            <div>
              <p className="display" style={{ fontSize: 22, fontStyle: "italic", opacity: 0.88, lineHeight: 1.5, maxWidth: 560 }}>
                pro is for the book you're actually finishing.
              </p>
              <div style={{ display: "flex", gap: 14, marginTop: 24, alignItems: "center", flexWrap: "wrap" }}>
                <StartWritingButton variant="pink" size="xl">
                  {cycle === "monthly" ? "start pro · $4.99 first month →" : "start pro · $120/yr →"}
                </StartWritingButton>
              </div>
              <div style={{ marginTop: 14, display: "inline-flex", gap: 8, alignItems: "center" }}>
                {[0, 1, 2, 3, 4].map((i) => <Star key={i} size={12} color="var(--clay)" />)}
                <span className="mono" style={{ fontSize: 11, opacity: 0.7 }}>
                  cancel in a click
                </span>
              </div>
            </div>
            <ProUnlocksPanel />
          </div>
        </div>
      </section>

      {/* Why pro */}
      <Section
        label="what you unlock"
        title="the five reasons pro exists"
        italicWord=""
        body="free already writes with you: the review room, inline edits, version history, and the editorial letter, all included. here's exactly what pro adds on top."
      >
        <div style={{ marginTop: 30 }}>
          <FeatureRow
            n="01"
            title="open expression, unlocked"
            accent="var(--pink-deep)"
            Icon={IconShield}
            body="the toggle for mature themes and darker material is locked by default on every plan. pro unlocks it. Quarkle's safety boundary stays fixed either way, this just removes the training-wheels version for writers who don't need it."
            tape="no content wall"
          />
          <FeatureRow
            n="02"
            title="every model, not just auto"
            accent="var(--violet)"
            Icon={IconRoom}
            body="free is auto only: quarkle picks a good model for the turn. pro opens the full picker in chat, ten-plus models including claude, gpt, grok, and kimi, so you choose per message instead of trusting an average."
          />
          <FeatureRow
            n="03"
            title="the deeper editorial reports"
            accent="var(--clay)"
            Icon={IconMemory}
            body="every plan gets the editorial letter and craft & voice reports. pro adds developmental (structure, characters, pacing) and full assessment: a promise ledger tracking every thread your book makes, and a tension map, scene by scene."
          />
          <FeatureRow
            n="04"
            title="a bigger Drawing Room"
            accent="var(--lime)"
            Icon={IconImage}
            body="every writer can make cover art, character portraits, and scenes from their manuscript. free includes 5 pictures a day and up to 10 kept per book. pro raises that to 50 pictures a day with no fixed gallery limit."
            tape="50 pictures a day"
          />
          <FeatureRow
            n="05"
            title="200x the daily usage"
            accent="var(--moss)"
            Icon={IconBolt}
            body="free is enough to actually try quarkle seriously. pro is enough to stop counting: 200x the daily token budget, so a long revision session doesn't hit a wall halfway through."
          />
        </div>
      </Section>

      {/* Not-a-chatbot comparison */}
      <section style={{ padding: "80px 40px", position: "relative", background: "var(--paper-3)", borderTop: "1.5px solid var(--ink)", borderBottom: "1.5px solid var(--ink)" }}>
        <div aria-hidden style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          <BaselineGrid w={1600} h={700} cell={56} color="var(--ink)" opacity={0.06} />
        </div>
        <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative" }}>
          <CornerPin>how quarkle is not a chatbot</CornerPin>
          <h2 className="tanker" style={{ fontSize: "clamp(44px, 5.6vw, 88px)", margin: "14px 0 24px", lineHeight: 0.96, letterSpacing: "-0.03em" }}>
            one model guessing,
            <br />
            vs. <span className="display" style={{ fontStyle: "italic", color: "var(--pink)" }}>twelve reading</span>.
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
            <div
              style={{
                background: "var(--paper-2)",
                border: "1.5px solid var(--ink)",
                borderRadius: 14,
                padding: "24px 26px",
                boxShadow: "6px 6px 0 var(--ink)",
              }}
            >
              <div className="caps" style={{ color: "var(--clay)", marginBottom: 10 }}>a generic chatbot</div>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 12 }}>
                {[
                  "asks one model to \"give feedback\" on your chapter",
                  "one voice, one bias, one suspiciously confident paragraph",
                  "forgets your dog-character the next time you open the app",
                  "offers to rewrite the whole thing · softens your voice",
                  "trains on what you paste in (unless you pay extra not to)",
                ].map((p) => (
                  <li key={p} style={{ display: "flex", gap: 10 }}>
                    <span style={{ color: "var(--clay)", flexShrink: 0 }}>×</span>
                    <span style={{ fontSize: 15, opacity: 0.88 }}>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div
              style={{
                background: "var(--paper-3)",
                color: "var(--ink)",
                border: "1.5px solid var(--ink)",
                borderRadius: 14,
                padding: "24px 26px",
                boxShadow: "6px 6px 0 var(--pink)",
              }}
            >
              <div className="caps" style={{ color: "var(--pink)", marginBottom: 10 }}>quarkle</div>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 12 }}>
                {[
                  "twelve specialists read your chapter in parallel",
                  "an AI consolidation pass drops contradictions and returns one focused note",
                  "your Story Atlas refreshes from manuscript characters, worlds, and chapter events",
                  "suggestions are proposals: yours in one keystroke, kept in another",
                  "no training, no selling, no reading between the lines of your life",
                ].map((p) => (
                  <li key={p} style={{ display: "flex", gap: 10 }}>
                    <span style={{ color: "var(--lime)", flexShrink: 0 }}>✓</span>
                    <span style={{ fontSize: 15 }}>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing split */}
      <section style={{ padding: "100px 40px", position: "relative" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 56, alignItems: "center" }}>
          <div>
            <span className="caps" style={{ color: "var(--pink)" }}>the price · honest</span>
            <h2 className="tanker" style={{ fontSize: "clamp(44px, 5.6vw, 92px)", margin: "14px 0 18px", lineHeight: 0.95 }}>
              <span className="display" style={{ fontStyle: "italic", color: "var(--pink)" }}>one</span> subscription.
              <br />
              no upsells. no tiers.
            </h2>
            <p className="display" style={{ fontSize: 19, fontStyle: "italic", opacity: 0.86, lineHeight: 1.55, maxWidth: 520 }}>
              studio (for teams) is the only plan above pro, and we only sell it when you ask. try pro for $4.99 the first month, cancel any time, keep every word you wrote either way.
            </p>
            <div style={{ marginTop: 24, display: "inline-flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <Button variant="subtle" size="md" onClick={() => { window.location.href = "/pricing"; }}>
                see all plans →
              </Button>
              <Button variant="subtle" size="md" onClick={() => { window.location.href = "mailto:team@quarkle.ai?subject=Quarkle%20Studio"; }}>
                talk to us about studio
              </Button>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <PricingCard cycle={cycle} setCycle={setCycle} />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section
        style={{
          padding: "120px 40px",
          background: "var(--navy)",
          color: "#F3ECDC",
          position: "relative",
          overflow: "hidden",
          textAlign: "center",
          borderTop: "1.5px solid var(--ink)",
        }}
      >
        <div aria-hidden style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          <BaselineGrid w={1600} h={700} cell={56} color="#F3ECDC" opacity={0.08} />
        </div>
        <div style={{ position: "relative", maxWidth: 820, margin: "0 auto" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
            <QuarklePlanet size={44} color="var(--pink)" ring="var(--paper-3)" />
            <span className="caps" style={{ color: "var(--lime)" }}>pro · the full room</span>
          </div>
          <h2 className="tanker" style={{ fontSize: "clamp(56px, 8vw, 140px)", margin: "0 0 18px", lineHeight: 0.9 }}>
            let's finish the <span className="display" style={{ fontStyle: "italic", color: "var(--pink)" }}>book</span>.
          </h2>
          <p className="display" style={{ fontSize: 20, fontStyle: "italic", opacity: 0.8, margin: "0 0 28px" }}>
            open expression, every model, and 200x the room to work. one subscription, cancel in a click.
          </p>
          <StartWritingButton variant="pink" size="xl">
            {cycle === "monthly" ? "start pro · $4.99 first month →" : "start pro · $120/yr →"}
          </StartWritingButton>
        </div>
      </section>

      <Footer />
    </div>
  );
}
